import { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { Text, Button, Badge, Card, CardHeader, Spinner } from '@fluentui/react-components';
import { BeakerRegular, ArrowClockwiseRegular, PlayRegular, ErrorCircleRegular } from '@fluentui/react-icons';
import { apiUrl } from '../utils/api-url';
import { useApiToken } from '../hooks/useApiToken';
import { getScoreColor } from '../utils/badge-config';

interface SimulationScore {
  score: number;
  reason: string;
}

interface SimulationResult {
  persona: string;
  persona_name: string;
  rounds: number;
  conversation_length: number;
  scores: {
    depth: SimulationScore;
    findings: SimulationScore;
    mapping: SimulationScore;
    quality: SimulationScore;
    engagement: SimulationScore;
    satisfaction: SimulationScore;
    overall: number;
    improvements: string[];
  };
  timestamp: string;
  prompt_version?: string;
}

interface SimulationRun {
  id: string;
  timestamp: string;
  promptVersion: string;
  results: SimulationResult[];
  averageOverall: number;
}

const CRITERIA = ['depth', 'findings', 'mapping', 'quality', 'engagement', 'satisfaction'] as const;
const CRITERIA_LABEL_IDS: Record<string, string> = {
  depth: 'simulation.criteria.depth',
  findings: 'simulation.criteria.findings',
  mapping: 'simulation.criteria.mapping',
  quality: 'simulation.criteria.quality',
  engagement: 'simulation.criteria.engagement',
  satisfaction: 'simulation.criteria.satisfaction',
};

export function SimulationResultsPage() {
  const intl = useIntl();
  const getToken = useApiToken();
  const [runs, setRuns] = useState<SimulationRun[]>([]);
  const [selectedRun, setSelectedRun] = useState<SimulationRun | null>(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadRuns();
  }, []);

  const loadRuns = async () => {
    setLoading(true);
    // Try API first, fall back to static file
    let loaded = false;
    try {
      const token = await getToken();
      const res = await fetch(apiUrl('/api/simulation/runs'), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data) && data.length > 0) {
          setRuns(data);
          setSelectedRun(data[data.length - 1]);
          loaded = true;
        }
      }
    } catch { /* fall through to static file */ }

    if (!loaded) {
      try {
        const res = await fetch('/assessment-simulation-results-v2.json');
        if (res.ok) {
          const results: SimulationResult[] = await res.json();
          if (results.length > 0) {
            const run: SimulationRun = {
              id: 'local-1',
              timestamp: results[0]?.timestamp ?? new Date().toISOString(),
              promptVersion: 'v2',
              results,
              averageOverall: results.reduce((s, r) => s + (r.scores?.overall ?? 0), 0) / results.length,
            };
            setRuns([run]);
            setSelectedRun(run);
          }
        }
      } catch { /* no results yet */ }
    }
    setLoading(false);
  };

  const triggerSimulation = async () => {
    setRunning(true);
    try {
      const token = await getToken();
      await fetch(apiUrl('/api/simulation/run'), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      // Poll for completion
      setTimeout(() => { loadRuns(); setRunning(false); }, 5000);
    } catch {
      setError('Failed to start simulation run.');
      setRunning(false);
    }
  };

  if (loading) return (
    <div className="page-loading">
      <Spinner size="large" label={intl.formatMessage({ id: 'simulation.loading' })} />
    </div>
  );

  return (
    <div className="page-root page-root--scrollable simulation-page">
      <div className="page-title-bar">
        <BeakerRegular className="icon-title text-brand" />
        <Text size={500} weight="semibold">{intl.formatMessage({ id: 'simulation.title' })}</Text>
        <div className="title-bar-actions">
          <Button appearance="subtle" icon={<ArrowClockwiseRegular />} onClick={loadRuns} title={intl.formatMessage({ id: 'simulation.refreshButton.title' })} />
          <Button appearance="primary" icon={<PlayRegular />} onClick={triggerSimulation} disabled={running}>
            {running
              ? intl.formatMessage({ id: 'simulation.runButton.running' })
              : intl.formatMessage({ id: 'simulation.runButton.idle' })}
          </Button>
        </div>
      </div>

      {error && (
        <div className="page-error">
          <ErrorCircleRegular />
          <Text className="text-danger">{error}</Text>
          <Button appearance="primary" onClick={loadRuns}>{intl.formatMessage({ id: 'simulation.retryButton' })}</Button>
        </div>
      )}

      {/* Run history — removed, now using trend chart as selector */}

      {/* Score trend — hero element at top */}
      {runs.length > 0 && (
        <div className="trend-hero">
          <div className="section-header">
            <Text size={400} weight="semibold">{intl.formatMessage({ id: 'simulation.trend.heading' })}</Text>
            <Text size={200} className="text-muted">{intl.formatMessage({ id: 'simulation.trend.iterationCount' }, { count: runs.length })}</Text>
          </div>
          <div className="trend-chart-hero">
            {(() => {
              // Scale bars relative to score range for visible differentiation
              const scores = runs.map(r => Number(r.averageOverall) || 0);
              const minScore = Math.max(Math.min(...scores) - 1, 0);
              const maxScore = Math.min(Math.max(...scores) + 1, 10);
              const range = maxScore - minScore || 1;
              return runs.map((run, i) => {
              const isSelected = run.id === selectedRun?.id;
              const score = Number(run.averageOverall) || 0;
              const prev = i > 0 ? (Number(runs[i - 1].averageOverall) || 0) : score;
              const delta = score - prev;
              const barHeight = Math.max(((score - minScore) / range) * 100, 5);
              return (
                <div
                  key={`${run.id}-${i}`}
                  className={`trend-item ${isSelected ? 'trend-item--selected' : ''}`}
                  onClick={() => setSelectedRun(run)}
                >
                  <div className="trend-bar-wrapper">
                    <div className="trend-bar-fill" data-score={score} style={{ height: `${barHeight}%` }} />
                  </div>
                  <Text size={100} weight="semibold">{score.toFixed(1)}</Text>
                  {i > 0 && Math.abs(delta) >= 0.1 && (
                    <Text size={100} className={delta > 0 ? 'text-success' : 'text-danger'}>
                      {delta > 0 ? '+' : ''}{delta.toFixed(1)}
                    </Text>
                  )}
                </div>
              );
              });
            })()}
          </div>
        </div>
      )}

      {selectedRun && (
        <>
          {/* Score cards */}
          <div className="dashboard-summary">
            <Card size="small" className="summary-card">
              <CardHeader
                header={<Text size={200} weight="semibold" className="text-muted">{intl.formatMessage({ id: 'simulation.scoreCard.averageScore' })}</Text>}
                description={
                  <Text size={600} weight="bold">
                    <Badge size="small" color={getScoreColor(selectedRun.averageOverall)}>
                      {selectedRun.averageOverall.toFixed(1)}/10
                    </Badge>
                  </Text>
                }
              />
            </Card>

            {/* Per-criteria averages */}
            {CRITERIA.map(criterion => {
              const avg = selectedRun.results.reduce((s, r) => {
                const c = r.scores?.[criterion];
                return s + (c?.score ?? 0);
              }, 0) / selectedRun.results.length;
              return (
                <Card key={criterion} size="small" className="summary-card">
                  <CardHeader
                    header={<Text size={200} weight="semibold" className="text-muted">{intl.formatMessage({ id: CRITERIA_LABEL_IDS[criterion] })}</Text>}
                    description={
                      <Text size={600} weight="bold">
                        <Badge size="small" color={getScoreColor(avg)}>{avg.toFixed(1)}</Badge>
                      </Text>
                    }
                  />
                </Card>
              );
            })}
          </div>

          {/* Per-persona results */}
          <div className="simulation-detail">
            <div className="section-header">
              <Text size={400} weight="semibold">{intl.formatMessage({ id: 'simulation.perPersona.heading' })}</Text>
            </div>
            <div className="table-scroll-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>{intl.formatMessage({ id: 'simulation.table.persona' })}</th>
                    <th>{intl.formatMessage({ id: 'simulation.table.overall' })}</th>
                    {CRITERIA.map(c => <th key={c}>{intl.formatMessage({ id: CRITERIA_LABEL_IDS[c] })}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {selectedRun.results.map(r => (
                    <tr key={r.persona}>
                      <td>
                        <Text weight="semibold">{r.persona_name}</Text>
                        <br />
                        <Text size={200} className="text-muted">{r.persona}</Text>
                      </td>
                      <td>
                        <Badge size="small" color={getScoreColor(r.scores?.overall ?? 0)}>
                          {r.scores?.overall ?? 'N/A'}
                        </Badge>
                      </td>
                      {CRITERIA.map(c => {
                        const score = r.scores?.[c]?.score ?? 0;
                        return (
                          <td key={c}>
                            <Badge size="small" color={getScoreColor(score)} appearance="outline">{score}</Badge>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Improvement suggestions */}
          <div className="simulation-detail">
            <div className="section-header">
              <Text size={400} weight="semibold">{intl.formatMessage({ id: 'simulation.improvements.heading' })}</Text>
            </div>
            {selectedRun.results.map(r => (
              <div key={r.persona} className="improvement-section">
                <Text size={300} weight="semibold">{r.persona_name}</Text>
                <ul className="improvement-list">
                  {(r.scores?.improvements ?? []).slice(0, 5).map((imp, i) => (
                    <li key={i}><Text size={200}>{imp}</Text></li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Old trend chart removed — now at top as hero element */}
        </>
      )}

      {runs.length === 0 && (
        <div className="page-empty">
          <BeakerRegular />
          <Text className="text-muted">{intl.formatMessage({ id: 'simulation.empty' })}</Text>
        </div>
      )}
    </div>
  );
}
